import {
  ALPHABET_WRITING_PLAYBACK_CONTRACT,
  ALPHABET_WRITING_TEMPLATE_ID,
  parseAlphabetWritingWordLayoutPolicy,
  type ApprovedWritingGuideRenderRelease,
} from '@crabs-ai/alphabet-writing-runtime/contracts';
import {
  parseProjectBindingV2,
  parseReleaseBundleManifest,
  type AlphabetWritingReleaseBundleManifest,
} from '@crabs-ai/alphabet-writing-runtime/release-bundle';
import { WRITING_GUIDE_RENDER_HOST_CONTRACT } from './writing-guide-render-host-contract';

export type ApprovedWritingGuideRelease = ApprovedWritingGuideRenderRelease & {
  bundleContentHash?: string;
  bundleManifestAssetPath?: string;
  wordLayoutPolicyAssetPath?: string;
};

type WritingGuideItemMetadata = {
  alphabetCase?: unknown;
  playbackContract?: unknown;
  rendererAssetPath?: unknown;
  rendererSourceHash?: unknown;
  rendererVersion?: unknown;
  runtimeSourceHash?: unknown;
  templateId?: unknown;
};

export type ApprovedWritingGuideReleaseResolution = {
  release: ApprovedWritingGuideRelease;
  releases: ApprovedWritingGuideRelease[];
  runtime: Record<string, unknown>;
  word: string;
};

export function resolveApprovedWritingGuideRelease(
  item: WritingGuideItemMetadata,
  authoringSpec: Record<string, unknown>,
): ApprovedWritingGuideReleaseResolution {
  if (authoringSpec.version !== 'alphabet.authoring_render_spec.v2') {
    throw new Error('현재 프로젝트에 승인된 v2 렌더 명세가 없습니다.');
  }

  const runtime = isRecord(authoringSpec.runtime)
    ? authoringSpec.runtime
    : null;
  if (!runtime) {
    throw new Error('따라쓰기 구조화 렌더 명세에 런타임이 없습니다.');
  }

  if (
    runtime.version === 'alphabet.authoring_runtime.v2' ||
    isProjectBindingV2(runtime.projectBinding)
  ) {
    return resolveBundleBoundWritingGuideRelease(item, authoringSpec, runtime);
  }

  const playbackContract = matchingMetadata(
    item.playbackContract,
    runtime.playbackContract,
  );
  const alphabetCase = matchingMetadata(item.alphabetCase, runtime.case);
  const rendererAssetPath = matchingMetadata(
    item.rendererAssetPath,
    runtime.rendererAssetPath,
  );
  const rendererSourceHash = matchingMetadata(
    item.rendererSourceHash,
    runtime.rendererSourceHash,
  );
  const rendererVersion = matchingMetadata(
    item.rendererVersion,
    runtime.rendererVersion,
  );
  const runtimeSourceHash = matchingMetadata(
    item.runtimeSourceHash,
    runtime.sourceStateHash,
  );
  const word = typeof authoringSpec.word === 'string' ? authoringSpec.word : '';
  const wordCase = resolveWordAlphabetCase(word);
  if (!wordCase || alphabetCase !== wordCase) {
    throw new Error('현재 프로젝트의 따라쓰기 단어 명세가 올바르지 않습니다.');
  }
  const sourceBindings = resolveRuntimeSourceBindings(runtime, wordCase);
  const releases = sourceBindings.map((binding) => {
    const release = WRITING_GUIDE_RENDER_HOST_CONTRACT.approvedReleases.find(
      (candidate) =>
        item.templateId === candidate.templateId &&
        playbackContract === candidate.playbackContract &&
        binding.alphabetCase === candidate.alphabetCase &&
        rendererAssetPath === candidate.rendererAssetPath &&
        rendererSourceHash === candidate.rendererSourceHash &&
        rendererVersion === candidate.rendererVersion &&
        binding.runtimeSourceHash === candidate.runtimeSourceHash &&
        (!binding.releaseId || binding.releaseId === candidate.releaseId),
    );
    if (!release || !sourceBindingMatchesRelease(binding, release)) {
      throw new Error(
        '현재 프로젝트의 따라쓰기 데이터가 승인된 릴리스 목록에 없습니다.',
      );
    }
    return release;
  });
  if (
    releases.some(
      (release) =>
        !sameCanonicalJson(
          authoringSpec.layoutPolicy,
          release.wordLayoutPolicy,
        ),
    )
  ) {
    throw new Error(
      '현재 프로젝트의 따라쓰기 레이아웃 정책이 승인본과 다릅니다.',
    );
  }

  const runtimeRelease = isRecord(runtime.release) ? runtime.release : null;
  if (
    releases.some(
      (release) =>
        runtime.version !== release.runtimeVersion ||
        runtime.playbackContract !== release.playbackContract ||
        runtime.rendererSourceHash !== release.rendererSourceHash ||
        runtime.rendererAssetPath !== release.rendererAssetPath ||
        runtime.rendererVersion !== release.rendererVersion,
    ) ||
    runtime.case !== wordCase ||
    runtime.sourceStateHash !== runtimeSourceHash ||
    runtimeRelease?.status !== 'approved' ||
    runtimeRelease.sourceStateHash !== runtimeSourceHash ||
    runtimeRelease.rendererSourceHash !== rendererSourceHash
  ) {
    throw new Error('현재 프로젝트의 구조화 렌더 명세가 승인본과 다릅니다.');
  }
  if (wordCase === 'mixed') {
    if (
      runtimeRelease.version !== 'alphabet.authoring_composite_release.v1' ||
      !sameCanonicalJson(runtimeRelease.sourceReleases, runtime.sourceReleases)
    ) {
      throw new Error('현재 프로젝트의 혼합 대소문자 렌더 명세가 다릅니다.');
    }
  } else if (runtime.sourceStateHash !== releases[0]?.runtimeSourceHash) {
    throw new Error('현재 프로젝트의 구조화 렌더 명세가 승인본과 다릅니다.');
  }

  const release = releases[0];
  if (!release) {
    throw new Error(
      '현재 프로젝트의 따라쓰기 데이터가 승인된 릴리스 목록에 없습니다.',
    );
  }
  return { release, releases, runtime, word };
}

export function assertApprovedWritingGuideRuntimeMatchesRelease(
  runtime: Record<string, unknown>,
  release: ApprovedWritingGuideRelease,
): void {
  const glyphs = isRecord(runtime.glyphs) ? runtime.glyphs : null;
  const glyphKeys = glyphs ? Object.keys(glyphs).sort() : [];
  const expectedGlyphKeys = alphabetForCase(release.alphabetCase)
    .split('')
    .sort();
  if (release.runtimeVersion === 'alphabet.authoring_runtime.v2') {
    if (
      runtime.case !== release.alphabetCase ||
      runtime.version !== release.runtimeVersion ||
      runtime.playbackContract !== release.playbackContract ||
      !sameCanonicalJson(glyphKeys, expectedGlyphKeys)
    ) {
      throw new Error(
        `승인된 따라쓰기 runtime 계약이 릴리스와 다릅니다. (${release.releaseId})`,
      );
    }
    return;
  }

  const runtimeRelease = isRecord(runtime.release) ? runtime.release : null;
  if (
    runtime.case !== release.alphabetCase ||
    runtime.version !== release.runtimeVersion ||
    runtime.playbackContract !== release.playbackContract ||
    runtime.sourceStateHash !== release.runtimeSourceHash ||
    runtime.rendererVersion !== release.rendererVersion ||
    runtime.rendererSourceHash !== release.rendererSourceHash ||
    runtime.rendererAssetPath !== release.rendererAssetPath ||
    runtimeRelease?.status !== 'approved' ||
    runtimeRelease.sourceStateHash !== release.runtimeSourceHash ||
    runtimeRelease.rendererSourceHash !== release.rendererSourceHash ||
    !sameCanonicalJson(glyphKeys, expectedGlyphKeys)
  ) {
    throw new Error(
      `승인된 따라쓰기 runtime 계약이 릴리스와 다릅니다. (${release.releaseId})`,
    );
  }
}

export function assertWritingGuideProjectRuntimeMatchesApprovedAsset(
  projectRuntime: Record<string, unknown>,
  word: string,
  approvedRuntime: Record<string, unknown>,
): void {
  const projectGlyphs = isRecord(projectRuntime.glyphs)
    ? projectRuntime.glyphs
    : null;
  const approvedGlyphs = isRecord(approvedRuntime.glyphs)
    ? approvedRuntime.glyphs
    : null;
  const requiredLetters = [...new Set(word)].sort();
  const projectGlyphKeys = projectGlyphs
    ? Object.keys(projectGlyphs).sort()
    : [];
  const hasEveryRequiredGlyph = requiredLetters.every((letter) =>
    projectGlyphKeys.includes(letter),
  );
  if (approvedRuntime.version === 'alphabet.authoring_runtime.v2') {
    if (
      !projectGlyphs ||
      !approvedGlyphs ||
      !hasEveryRequiredGlyph ||
      projectRuntime.version !== approvedRuntime.version ||
      projectRuntime.playbackContract !== approvedRuntime.playbackContract ||
      !sameCanonicalJson(
        projectRuntime.wordLayoutPolicy,
        approvedRuntime.wordLayoutPolicy,
      ) ||
      !sameCanonicalJson(
        projectRuntime.lineAnchors,
        approvedRuntime.lineAnchors,
      )
    ) {
      throw new Error(
        '현재 프로젝트의 따라쓰기 runtime이 승인된 불변 에셋과 다릅니다.',
      );
    }
    assertProjectGlyphsMatchApprovedRuntime(projectGlyphs, approvedGlyphs);
    return;
  }
  if (
    !projectGlyphs ||
    !approvedGlyphs ||
    !hasEveryRequiredGlyph ||
    !sameCanonicalJson(
      projectRuntime.lineAnchors,
      approvedRuntime.lineAnchors,
    ) ||
    !sameCanonicalJson(projectRuntime.release, approvedRuntime.release) ||
    projectRuntime.case !== approvedRuntime.case ||
    projectRuntime.sourceStateSavedAt !== approvedRuntime.sourceStateSavedAt
  ) {
    throw new Error(
      '현재 프로젝트의 따라쓰기 runtime이 승인된 불변 에셋과 다릅니다.',
    );
  }
  for (const letter of projectGlyphKeys) {
    if (!sameCanonicalJson(projectGlyphs[letter], approvedGlyphs[letter])) {
      throw new Error(
        `현재 프로젝트의 ${letter} 글자 데이터가 승인된 불변 에셋과 다릅니다.`,
      );
    }
  }
}

export type ApprovedWritingGuideRuntimeSource = {
  release: ApprovedWritingGuideRelease;
  runtime: Record<string, unknown>;
};

export function assertWritingGuideProjectRuntimeMatchesApprovedAssets(
  projectRuntime: Record<string, unknown>,
  word: string,
  approvedSources: ApprovedWritingGuideRuntimeSource[],
): void {
  if (approvedSources.length === 1) {
    assertWritingGuideProjectRuntimeMatchesApprovedAsset(
      projectRuntime,
      word,
      approvedSources[0]!.runtime,
    );
    return;
  }

  if (
    approvedSources.every(
      ({ release }) =>
        release.runtimeVersion === 'alphabet.authoring_runtime.v2',
    )
  ) {
    assertBundleBoundMixedRuntime(projectRuntime, word, approvedSources);
    return;
  }

  const wordCase = resolveWordAlphabetCase(word);
  const projectGlyphs = isRecord(projectRuntime.glyphs)
    ? projectRuntime.glyphs
    : null;
  const requiredLetters = [...new Set(word)].sort();
  const projectGlyphKeys = projectGlyphs
    ? Object.keys(projectGlyphs).sort()
    : [];
  const hasEveryRequiredGlyph = requiredLetters.every((letter) =>
    projectGlyphKeys.includes(letter),
  );
  const sourceBindings = Array.isArray(projectRuntime.sourceReleases)
    ? projectRuntime.sourceReleases.filter(isRecord)
    : [];
  const runtimeRelease = isRecord(projectRuntime.release)
    ? projectRuntime.release
    : null;
  if (
    wordCase !== 'mixed' ||
    projectRuntime.case !== 'mixed' ||
    !projectGlyphs ||
    !hasEveryRequiredGlyph ||
    sourceBindings.length !== approvedSources.length ||
    runtimeRelease?.version !== 'alphabet.authoring_composite_release.v1' ||
    runtimeRelease.status !== 'approved' ||
    runtimeRelease.sourceStateHash !== projectRuntime.sourceStateHash ||
    !sameCanonicalJson(runtimeRelease.sourceReleases, sourceBindings) ||
    projectRuntime.sourceStateSavedAt !== null
  ) {
    throw new Error(
      '현재 프로젝트의 혼합 따라쓰기 runtime이 승인된 불변 에셋과 다릅니다.',
    );
  }

  for (const source of approvedSources) {
    const binding = sourceBindings.find(
      (candidate) => candidate.alphabetCase === source.release.alphabetCase,
    );
    if (
      !binding ||
      !sourceBindingMatchesRelease(binding, source.release) ||
      !sameCanonicalJson(projectRuntime.lineAnchors, source.runtime.lineAnchors)
    ) {
      throw new Error(
        `현재 프로젝트의 ${source.release.alphabetCase} 승인 출처가 불변 에셋과 다릅니다.`,
      );
    }
    const approvedGlyphs = isRecord(source.runtime.glyphs)
      ? source.runtime.glyphs
      : {};
    for (const letter of projectGlyphKeys) {
      if (alphabetCaseForGlyph(letter) !== source.release.alphabetCase)
        continue;
      if (!sameCanonicalJson(projectGlyphs[letter], approvedGlyphs[letter])) {
        throw new Error(
          `현재 프로젝트의 ${letter} 글자 데이터가 승인된 불변 에셋과 다릅니다.`,
        );
      }
    }
  }
}

export function buildApprovedWritingGuideRenderRuntime(
  projectRuntime: Record<string, unknown>,
  word: string,
  approvedSources: ApprovedWritingGuideRuntimeSource[],
): Record<string, unknown> {
  assertWritingGuideProjectRuntimeMatchesApprovedAssets(
    projectRuntime,
    word,
    approvedSources,
  );
  if (approvedSources.length === 1) return approvedSources[0]!.runtime;
  return {
    ...projectRuntime,
    glyphs: Object.assign(
      {},
      ...approvedSources.map(({ runtime }) =>
        isRecord(runtime.glyphs) ? runtime.glyphs : {},
      ),
    ),
  };
}

export function buildWritingGuideCompositeRuntimeSource(
  bindings: Array<Record<string, unknown>>,
): string {
  return `alphabet.authoring_composite_runtime.v1\n${bindings
    .map(
      (binding) =>
        `${String(binding.alphabetCase ?? '')}:${String(
          binding.runtimeSourceHash ?? '',
        )}`,
    )
    .join('\n')}`;
}

export function sameCanonicalJson(left: unknown, right: unknown): boolean {
  return (
    JSON.stringify(toCanonicalJson(left)) ===
    JSON.stringify(toCanonicalJson(right))
  );
}

function matchingMetadata(primary: unknown, structured: unknown): unknown {
  if (
    primary !== undefined &&
    primary !== null &&
    structured !== undefined &&
    structured !== null &&
    primary !== structured
  ) {
    throw new Error('현재 프로젝트의 구조화 렌더 명세가 승인본과 다릅니다.');
  }
  return primary ?? structured;
}

function resolveRuntimeSourceBindings(
  runtime: Record<string, unknown>,
  alphabetCase: 'lowercase' | 'uppercase' | 'mixed',
): Array<Record<string, unknown>> {
  if (alphabetCase !== 'mixed') {
    return [
      {
        alphabetCase,
        runtimeSourceHash: runtime.sourceStateHash,
      },
    ];
  }
  const bindings = Array.isArray(runtime.sourceReleases)
    ? runtime.sourceReleases.filter(isRecord)
    : [];
  const requiredCases = ['lowercase', 'uppercase'];
  if (
    bindings.length !== requiredCases.length ||
    bindings.some(
      (binding, index) =>
        binding.alphabetCase !== requiredCases[index] ||
        typeof binding.runtimeSourceHash !== 'string',
    )
  ) {
    throw new Error('현재 프로젝트의 혼합 대소문자 승인 출처가 없습니다.');
  }
  return bindings;
}

function sourceBindingMatchesRelease(
  binding: Record<string, unknown>,
  release: ApprovedWritingGuideRelease,
): boolean {
  if (release.bundleContentHash) {
    return (
      binding.alphabetCase === release.alphabetCase &&
      binding.bundleContentHash === release.bundleContentHash &&
      binding.releaseId === release.releaseId
    );
  }
  return (
    binding.alphabetCase === release.alphabetCase &&
    binding.runtimeSourceHash === release.runtimeSourceHash &&
    (binding.releaseId === undefined ||
      binding.releaseId === release.releaseId) &&
    (binding.runtimeAssetPath === undefined ||
      binding.runtimeAssetPath === release.runtimeAssetPath) &&
    (binding.runtimeAssetSourceHash === undefined ||
      binding.runtimeAssetSourceHash === release.runtimeAssetSourceHash)
  );
}

function resolveBundleBoundWritingGuideRelease(
  item: WritingGuideItemMetadata,
  authoringSpec: Record<string, unknown>,
  runtime: Record<string, unknown>,
): ApprovedWritingGuideReleaseResolution {
  const word = typeof authoringSpec.word === 'string' ? authoringSpec.word : '';
  const wordCase = resolveWordAlphabetCase(word);
  if (!wordCase) {
    throw new Error('현재 프로젝트의 따라쓰기 단어 명세가 올바르지 않습니다.');
  }
  const requiredCases = casesForWordCase(wordCase);
  const binding = parseProjectBindingV2(runtime.projectBinding);
  if (
    !sameCanonicalJson(
      binding.sources.map(({ alphabetCase }) => alphabetCase).sort(),
      requiredCases,
    )
  ) {
    throw new Error(
      '현재 프로젝트의 따라쓰기 번들 바인딩이 올바르지 않습니다.',
    );
  }
  if (!Array.isArray(runtime.bundleManifests)) {
    throw new Error('현재 프로젝트의 따라쓰기 번들 명세가 없습니다.');
  }
  const manifests = runtime.bundleManifests.map(parseReleaseBundleManifest);
  if (manifests.length !== requiredCases.length) {
    throw new Error('현재 프로젝트의 따라쓰기 번들 명세가 올바르지 않습니다.');
  }

  const layoutPolicy = authoringSpec.layoutPolicy;
  const rendererSourceHash = matchingMetadata(
    item.rendererSourceHash,
    runtime.rendererSourceHash,
  );
  const rendererVersion = matchingMetadata(
    item.rendererVersion,
    runtime.rendererVersion,
  );
  const rendererAssetPath = matchingMetadata(
    item.rendererAssetPath,
    runtime.rendererAssetPath,
  );
  const runtimeSourceHash = matchingMetadata(
    item.runtimeSourceHash,
    runtime.sourceStateHash,
  );
  if (
    item.templateId !== ALPHABET_WRITING_TEMPLATE_ID ||
    matchingMetadata(item.playbackContract, runtime.playbackContract) !==
      ALPHABET_WRITING_PLAYBACK_CONTRACT ||
    matchingMetadata(item.alphabetCase, runtime.case) !== wordCase ||
    runtime.version !== 'alphabet.authoring_runtime.v2' ||
    !sameCanonicalJson(runtime.wordLayoutPolicy, layoutPolicy) ||
    typeof runtimeSourceHash !== 'string'
  ) {
    throw new Error('현재 프로젝트의 구조화 렌더 명세가 승인본과 다릅니다.');
  }

  const releases = binding.sources.map((source) => {
    const manifest = manifests.find(
      (candidate) => candidate.alphabetCase === source.alphabetCase,
    );
    if (
      !manifest ||
      manifest.bundleContentHash !== source.bundleContentHash ||
      manifest.releaseId !== source.releaseId
    ) {
      throw new Error(
        '현재 프로젝트의 따라쓰기 번들 바인딩이 올바르지 않습니다.',
      );
    }
    return bundleManifestToApprovedRelease(manifest, layoutPolicy);
  });
  if (
    releases.some(
      (release) =>
        release.rendererSourceHash !== rendererSourceHash ||
        release.rendererVersion !== rendererVersion ||
        release.playbackContract !== runtime.playbackContract,
    ) ||
    !releases.some((release) => release.rendererAssetPath === rendererAssetPath)
  ) {
    throw new Error('현재 프로젝트의 구조화 렌더 명세가 승인본과 다릅니다.');
  }
  if (
    releases.length === 1 &&
    runtimeSourceHash !== releases[0]!.bundleContentHash
  ) {
    throw new Error(
      '현재 프로젝트의 따라쓰기 번들 바인딩이 올바르지 않습니다.',
    );
  }

  return { release: releases[0]!, releases, runtime, word };
}

function bundleManifestToApprovedRelease(
  manifest: AlphabetWritingReleaseBundleManifest,
  layoutPolicy: unknown,
): ApprovedWritingGuideRelease {
  const runtimeFile = requireBundleRole(manifest, 'runtime');
  const rendererFile = requireBundleRole(manifest, 'renderer');
  const policyFile = requireBundleRole(manifest, 'wordLayoutPolicy');
  let approvedLayoutPolicy: ApprovedWritingGuideRelease['wordLayoutPolicy'];
  try {
    approvedLayoutPolicy = parseAlphabetWritingWordLayoutPolicy(layoutPolicy);
  } catch {
    throw new Error(
      '현재 프로젝트의 따라쓰기 레이아웃 정책이 승인본과 다릅니다.',
    );
  }
  const root = `/agent-workflow/alphabet-authoring/bundles/${manifest.bundleContentHash.replace(':', '-')}`;
  return {
    alphabetCase: manifest.alphabetCase,
    bundleContentHash: manifest.bundleContentHash,
    bundleManifestAssetPath: `${root}/manifest.v1.json`,
    playbackContract: ALPHABET_WRITING_PLAYBACK_CONTRACT,
    releaseId: manifest.releaseId,
    rendererAssetPath: `${root}/${rendererFile.path}`,
    rendererSourceHash: rendererFile.sha256,
    rendererVersion: manifest.rendererVersion,
    runtimeAssetPath: `${root}/${runtimeFile.path}`,
    runtimeAssetSourceHash: runtimeFile.sha256,
    runtimeSourceHash: manifest.bundleContentHash,
    runtimeVersion: manifest.runtimeVersion,
    templateId: ALPHABET_WRITING_TEMPLATE_ID,
    wordLayoutPolicy: approvedLayoutPolicy,
    wordLayoutPolicyAssetPath: `${root}/${policyFile.path}`,
    wordLayoutPolicySourceHash: policyFile.sha256,
    wordLayoutPolicyVersion: 'alphabet.word_layout_policy.v1',
  };
}

function requireBundleRole(
  manifest: AlphabetWritingReleaseBundleManifest,
  role: string,
) {
  const file = manifest.files[role];
  if (!file) throw new Error(`따라쓰기 번들에 ${role} 파일이 없습니다.`);
  return file;
}

function assertBundleBoundMixedRuntime(
  projectRuntime: Record<string, unknown>,
  word: string,
  approvedSources: ApprovedWritingGuideRuntimeSource[],
): void {
  if (
    resolveWordAlphabetCase(word) !== 'mixed' ||
    projectRuntime.case !== 'mixed' ||
    projectRuntime.version !== 'alphabet.authoring_runtime.v2'
  ) {
    throw new Error(
      '현재 프로젝트의 혼합 따라쓰기 runtime이 승인된 불변 에셋과 다릅니다.',
    );
  }
  const binding = parseProjectBindingV2(projectRuntime.projectBinding);
  const projectGlyphs = isRecord(projectRuntime.glyphs)
    ? projectRuntime.glyphs
    : null;
  if (!projectGlyphs || binding.sources.length !== approvedSources.length) {
    throw new Error(
      '현재 프로젝트의 혼합 따라쓰기 runtime이 승인된 불변 에셋과 다릅니다.',
    );
  }
  for (const source of approvedSources) {
    const sourceBinding = binding.sources.find(
      (candidate) => candidate.alphabetCase === source.release.alphabetCase,
    );
    const approvedGlyphs = isRecord(source.runtime.glyphs)
      ? source.runtime.glyphs
      : null;
    if (
      !sourceBinding ||
      !sourceBindingMatchesRelease(sourceBinding, source.release) ||
      !approvedGlyphs ||
      !sameCanonicalJson(
        projectRuntime.wordLayoutPolicy,
        source.runtime.wordLayoutPolicy,
      ) ||
      !sameCanonicalJson(projectRuntime.lineAnchors, source.runtime.lineAnchors)
    ) {
      throw new Error(
        `현재 프로젝트의 ${source.release.alphabetCase} 승인 출처가 불변 에셋과 다릅니다.`,
      );
    }
    for (const letter of [...new Set(word)]) {
      if (alphabetCaseForGlyph(letter) !== source.release.alphabetCase)
        continue;
      if (!sameCanonicalJson(projectGlyphs[letter], approvedGlyphs[letter])) {
        throw new Error(
          `현재 프로젝트의 ${letter} 글자 데이터가 승인된 불변 에셋과 다릅니다.`,
        );
      }
    }
  }
}

function assertProjectGlyphsMatchApprovedRuntime(
  projectGlyphs: Record<string, unknown>,
  approvedGlyphs: Record<string, unknown>,
): void {
  for (const letter of Object.keys(projectGlyphs)) {
    if (!sameCanonicalJson(projectGlyphs[letter], approvedGlyphs[letter])) {
      throw new Error(
        `현재 프로젝트의 ${letter} 글자 데이터가 승인된 불변 에셋과 다릅니다.`,
      );
    }
  }
}

function isProjectBindingV2(value: unknown): boolean {
  return (
    isRecord(value) && value.version === 'alphabet.authoring_project_binding.v2'
  );
}

function casesForWordCase(
  wordCase: 'lowercase' | 'uppercase' | 'mixed',
): Array<'lowercase' | 'uppercase'> {
  if (wordCase === 'mixed') return ['lowercase', 'uppercase'];
  return [wordCase];
}

function resolveWordAlphabetCase(
  word: string,
): 'lowercase' | 'uppercase' | 'mixed' | null {
  if (!/^[A-Za-z]+$/.test(word)) return null;
  const hasLowercase = /[a-z]/.test(word);
  const hasUppercase = /[A-Z]/.test(word);
  if (hasLowercase && hasUppercase) return 'mixed';
  return hasUppercase ? 'uppercase' : 'lowercase';
}

function alphabetCaseForGlyph(letter: string): 'lowercase' | 'uppercase' {
  return /[A-Z]/.test(letter) ? 'uppercase' : 'lowercase';
}

function alphabetForCase(alphabetCase: string): string {
  return alphabetCase === 'uppercase'
    ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    : 'abcdefghijklmnopqrstuvwxyz';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toCanonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(toCanonicalJson);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, toCanonicalJson(value[key])]),
  );
}
